import React, { useEffect, useState, Fragment } from 'react';
import { ChevronDownIcon, CheckCircle2Icon, StarIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { FadeIn } from './FadeIn';
export function Pricing() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'pro'>('pro');
  const [timeLeft, setTimeLeft] = useState({
    h: 23,
    m: 59,
    s: 58
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let {
          h,
          m,
          s
        } = prev;
        if (s > 0) s--;else {
          s = 59;
          if (m > 0) m--;else {
            m = 59;
            if (h > 0) h--;
          }
        }
        return {
          h,
          m,
          s
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (val: number) => val.toString().padStart(2, '0');
  const units = [{
    value: timeLeft.h,
    label: 'hs'
  }, {
    value: timeLeft.m,
    label: 'min'
  }, {
    value: timeLeft.s,
    label: 'seg'
  }];
  return <section id="pricing" className="py-14 md:py-20 bg-surfaceLight/30 border-t border-slate-200 relative overflow-hidden" aria-label="Precios y planes" data-id="element-257">
      <div className="container mx-auto px-4 max-w-6xl relative z-10" data-id="element-258">
        <FadeIn data-id="element-259">
          <div className="text-center mb-8" data-id="element-260">
            <p className="text-lg md:text-2xl font-extrabold text-coral-dark mb-5" data-id="element-261">
              ⏰ PRECIO DE LANZAMIENTO — CIERRA EN:
            </p>
            <motion.div className="flex items-center justify-center gap-2.5 md:gap-4 text-4xl md:text-6xl font-heading font-extrabold text-coral-dark" animate={{
            scale: [1, 1.04, 1]
          }} transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-262">
              {units.map((u, i) => <Fragment key={u.label} data-id="element-263">
                  {i > 0 && <span className="text-coral/40 pb-7 md:pb-9" data-id="element-264">:</span>}
                  <div className="bg-white border-2 border-coral/30 rounded-2xl px-4 py-4 md:px-6 md:py-5 min-w-[84px] md:min-w-[112px] shadow-soft" data-id="element-265">
                    <motion.span key={`${u.label}-${u.value}`} className="block tabular-nums leading-none" initial={{
                  scale: 1.25,
                  opacity: 0.5
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  duration: 0.35,
                  ease: 'easeOut'
                }} data-id="element-266">
                      {formatTime(u.value)}
                    </motion.span>
                    <span className="text-[11px] md:text-xs block text-slate-500 font-body font-bold mt-2 uppercase tracking-widest" data-id="element-267">
                      {u.label}
                    </span>
                  </div>
                </Fragment>)}
            </motion.div>
            <p className="text-coral-dark mt-5 text-sm md:text-base font-bold" data-id="element-268">
              ⚠️ Después sube $10.000 ARS. No vuelve.
            </p>
          </div>
        </FadeIn>

        <FadeIn data-id="element-269">
          <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-slate-900 text-center mb-8" data-id="element-270">
            ELEGÍ TU SISTEMA
          </h2>
        </FadeIn>

        <div id="elegir-plan" className="max-w-3xl mx-auto mb-14" role="radiogroup" aria-label="Seleccionar plan" data-id="element-271">
          <FadeIn delay={0.05} data-id="element-272">
            <div className="mb-6 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm" data-id="element-273">
              <div className="flex justify-between text-sm mb-3" data-id="element-274">
                <span className="text-primary-dark font-extrabold flex items-center gap-2" data-id="element-275">
                  <span className="w-2.5 h-2.5 rounded-full bg-primary animate-pulse" data-id="element-276"></span>
                  Cupos al precio de lanzamiento — Quedan pocos
                </span>
                <span className="text-slate-900 font-extrabold" data-id="element-277">72%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden" data-id="element-278">
                <div className="bg-primary h-full rounded-full" style={{
                width: '72%'
              }} data-id="element-279"></div>
              </div>
              <p className="text-xs text-slate-500 mt-3 text-center font-bold" data-id="element-280">
                72% de los cupos al precio de lanzamiento ya tomados
              </p>
            </div>
          </FadeIn>

          {/* Pack 1 — Basic */}
          <FadeIn delay={0.1} data-id="element-281">
            <button onClick={() => setSelectedPlan('basic')} role="radio" aria-checked={selectedPlan === 'basic'} className={`w-full text-left rounded-[1.75rem] p-5 mb-5 transition-all duration-300 border-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${selectedPlan === 'basic' ? 'border-primary shadow-soft-lg bg-white' : 'border-slate-200 bg-white/50 hover:border-slate-300'}`} data-id="element-282">
              <div className="flex items-start gap-4" data-id="element-283">
                <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selectedPlan === 'basic' ? 'border-primary' : 'border-slate-300'}`} data-id="element-284">
                  {selectedPlan === 'basic' && <div className="w-2.5 h-2.5 rounded-full bg-primary" data-id="element-285"></div>}
                </div>

                <div className="flex-1 min-w-0" data-id="element-286">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3" data-id="element-287">
                    <div data-id="element-288">
                      <h3 className="font-heading text-lg md:text-xl font-extrabold text-slate-900 mb-1" data-id="element-289">
                        📘 MEGAPACK MATEMÁTICAS SIN LÁGRIMAS
                      </h3>
                      <p className="text-slate-600 font-bold text-sm mb-2.5" data-id="element-290">
                        5 Tomos + 3 Bonos
                      </p>
                      <div className="bg-slate-50 px-3 py-2 rounded-lg border border-slate-100 text-xs text-slate-600 inline-block" data-id="element-291">
                        <span className="font-extrabold text-slate-900" data-id="element-292">
                          Ideal para:
                        </span>{' '}
                        Padres que quieren dejar de improvisar y acompañar con
                        método desde hoy.
                      </div>
                    </div>
                    <div className="md:text-right shrink-0 mt-1 md:mt-0" data-id="element-293">
                      <span aria-hidden="true" className="text-slate-400 line-through text-xs block mb-0.5 font-bold" data-id="element-294">
                        $49.990
                      </span>
                      <span className="text-3xl font-extrabold text-slate-900 font-heading" data-id="element-295">
                        $19.990
                      </span>
                      <span className="text-sm text-slate-500 ml-1 font-bold" data-id="element-296">
                        ARS
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          </FadeIn>

          {/* Pack 2 — Pro (dominant) */}
          <FadeIn delay={0.2} data-id="element-297">
            <div id="pack-pro" className="relative my-5 scroll-mt-24" data-id="element-298">
              <div className="absolute -inset-1 rounded-[2.5rem] opacity-50 blur-lg pointer-events-none" aria-hidden="true" style={{
              background: 'linear-gradient(135deg, rgba(245,158,11,0.3) 0%, rgba(45,212,191,0.2) 100%)'
            }} data-id="element-299"></div>

              <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10" data-id="element-300">
                <span className="bg-amber text-white font-extrabold px-6 py-2 rounded-full text-xs uppercase tracking-widest whitespace-nowrap shadow-glow-amber flex items-center gap-2" data-id="element-301">
                  ⭐ El más elegido
                </span>
              </div>

              <button onClick={() => setSelectedPlan('pro')} role="radio" aria-checked={selectedPlan === 'pro'} className={`relative w-full text-left rounded-[1.75rem] p-6 transition-all duration-300 border-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber ${selectedPlan === 'pro' ? 'border-amber shadow-soft-lg bg-white' : 'border-amber/30 bg-white/80 hover:border-amber/50'}`} data-id="element-302">
                <div className="flex items-start gap-4" data-id="element-303">
                  <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selectedPlan === 'pro' ? 'border-amber' : 'border-slate-300'}`} data-id="element-304">
                    {selectedPlan === 'pro' && <div className="w-2.5 h-2.5 rounded-full bg-amber" data-id="element-305"></div>}
                  </div>

                  <div className="flex-1 min-w-0" data-id="element-306">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4" data-id="element-307">
                      <div data-id="element-308">
                        <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900 mb-1" data-id="element-309">
                          🏆 MEGAPACK PREMIUM
                        </h3>
                        <p className="text-amber-dark text-sm font-extrabold mb-2.5" data-id="element-310">
                          5 Tomos + 3 Bonos + Kit Acelerador Matemático
                        </p>
                        <div className="bg-amber/5 px-3 py-2 rounded-lg border border-amber/20 text-xs text-slate-700 inline-block" data-id="element-311">
                          <span className="font-extrabold text-slate-900" data-id="element-312">
                            Ideal para:
                          </span>{' '}
                          Padres que quieren el sistema completo con todas las
                          herramientas listas desde el día 1.
                        </div>
                      </div>
                      <div className="md:text-right shrink-0 mt-1 md:mt-0" data-id="element-313">
                        <span aria-hidden="true" className="text-slate-400 line-through text-xs block mb-0.5 font-bold" data-id="element-314">
                          $79.990
                        </span>
                        <span className="text-4xl md:text-5xl font-extrabold text-amber-dark font-heading leading-none" data-id="element-315">
                          $29.990
                        </span>
                        <span className="text-xs text-amber-dark/80 ml-1 block mt-1 font-bold" data-id="element-316">
                          ARS
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-317">
            <a id="cta-comprar" href={selectedPlan === 'pro' ? 'https://fegion.shop/cart/placeholder-pro:1' : 'https://fegion.shop/cart/placeholder-basic:1'} className={`block w-full mt-6 py-4 text-center rounded-2xl font-extrabold transition-all duration-300 text-lg md:text-xl focus-visible:outline-2 focus-visible:outline-offset-2 ${selectedPlan === 'pro' ? 'bg-amber text-white hover:bg-amber-dark focus-visible:outline-amber shadow-glow-amber hover:-translate-y-1' : 'bg-primary text-white hover:bg-primary-dark focus-visible:outline-primary shadow-glow-primary hover:-translate-y-1'}`} data-id="element-318">
              {selectedPlan === 'pro' ? '→ QUIERO EL PREMIUM COMPLETO' : '→ LO QUIERO'}
            </a>
          </FadeIn>

          <FadeIn delay={0.45} data-id="element-319">
            <div className="mt-6 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-sm text-slate-500 font-bold" data-id="element-320">
              <div className="flex items-center gap-2" data-id="element-321">
                <span className="text-primary" aria-hidden="true" data-id="element-322">
                  🛡️
                </span>
                <span data-id="element-323">Garantía 7 días</span>
              </div>
              <span className="text-slate-300" aria-hidden="true" data-id="element-324">
                ·
              </span>
              <div className="flex items-center gap-2" data-id="element-325">
                <span className="text-primary" aria-hidden="true" data-id="element-326">
                  ⚡
                </span>
                <span data-id="element-327">Acceso de inmediato</span>
              </div>
              <span className="text-slate-300" aria-hidden="true" data-id="element-328">
                ·
              </span>
              <div className="flex items-center gap-2" data-id="element-329">
                <span className="text-primary" aria-hidden="true" data-id="element-330">
                  🔒
                </span>
                <span data-id="element-331">Pago 100% seguro</span>
              </div>
            </div>
          </FadeIn>
        </div>

        {/* Includes Comparison */}
        <FadeIn delay={0.3} data-id="element-332">
          <div className="max-w-5xl mx-auto mb-14" data-id="element-333">
            <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900 mb-8 text-center" data-id="element-334">
              ✅ ¿QUÉ INCLUYE CADA UNO?
            </h3>

            <div className="grid md:grid-cols-2 gap-5 md:gap-6" data-id="element-335">
              {/* Base */}
              <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-soft" data-id="element-336">
                <h4 className="font-heading text-xl font-extrabold text-slate-900 mb-2" data-id="element-337">
                  📘 MEGAPACK MATEMÁTICAS SIN LÁGRIMAS
                </h4>

                <ul className="space-y-3" data-id="element-338">
                  {['5 Tomos completos (+300 páginas)', 'Bono 1 — Mapa Rápido de Bloqueos Matemáticos', 'Bono 2 — Guía de Frases que Calman y Enseñan', 'Bono 3 — Plan Express de 7 Días para Bajar la Frustración'].map((item, i) => <li key={i} className="flex items-start gap-2.5 text-slate-700 text-sm font-medium" data-id="element-339">
                      <CheckCircle2Icon className="w-5 h-5 text-primary shrink-0" data-id="element-340" />
                      <span data-id="element-341">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* Pro */}
              <div className="bg-amber/5 rounded-3xl p-6 border border-amber/30 relative overflow-hidden shadow-soft" data-id="element-342">
                <h4 className="font-heading text-xl font-extrabold text-amber-dark mb-2" data-id="element-343">
                  ⚡ KIT ACELERADOR MATEMÁTICO
                </h4>
                <p className="text-slate-600 text-sm font-bold mb-4 border-b border-amber/20 pb-3" data-id="element-344">
                  Incluido solo en la versión Premium
                </p>
                <ul className="space-y-3 relative z-10" data-id="element-345">
                  {['20 Tarjetas de Rescate — protocolos exactos para cada tipo de bloqueo', '15 Fichas de Misión — práctica contextual lista para usar', 'Cuaderno de Práctica Dopamina — sesiones estructuradas de 10 minutos', 'Sistema de Logros estilo videojuego — motivación sostenida en el tiempo', 'Guiones de Rescate Emocional — qué decir exactamente en los momentos difíciles', 'Plantillas de Evaluación Rápida — seguimiento del progreso real'].map((item, i) => <li key={i} className="flex items-start gap-2.5 text-slate-800 text-sm font-bold" data-id="element-346">
                      <CheckCircle2Icon className="w-5 h-5 text-amber shrink-0" data-id="element-347" />
                      <span data-id="element-348">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </FadeIn>

        {/* Testimonials */}
        <FadeIn delay={0.4} data-id="element-349">
          <div className="max-w-5xl mx-auto mb-14" data-id="element-350">
            <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900 mb-8 text-center" data-id="element-351">
              ❤️ MÁS PADRES QUE YA LO TIENEN
            </h3>
            <div className="grid md:grid-cols-3 gap-5" data-id="element-352">
              {[{
              name: 'Diego V.',
              loc: '📍 Salta',
              plan: 'Sistema Premium',
              text: 'Pegué las tarjetas del Kit Acelerador en la mesa y mi hijo las usó solo para preparar el examen. No me llamó una sola vez para preguntarme cómo se hacía. Primera vez que eso pasa.'
            }, {
              name: 'Fernanda B.',
              loc: '📍 Neuquén',
              plan: 'Sistema Completo',
              text: 'La Guía de Frases me eliminó el 80% de las peleas. No tuve un solo llanto en las últimas 3 semanas desde que cambié cómo le hablo durante la tarea.'
            }, {
              name: 'Roberto F.',
              loc: '📍 Tucumán',
              plan: 'Sistema Completo',
              text: 'Con el Tomo 5 me di cuenta que no tenía ninguna rutina real. Solo improvisaba cada día. Ahora tenemos 15 minutos fijos y en un mes el cambio fue enorme.'
            }].map((t, i) => <div key={i} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-soft" data-id="element-353">
                  <div className="flex gap-1 mb-3" data-id="element-354">
                    {[...Array(5)].map((_, j) => <StarIcon key={j} className="w-4 h-4 text-amber fill-amber" data-id="element-355" />)}
                  </div>
                  <p className="text-slate-700 mb-4 text-sm italic leading-snug" data-id="element-356">
                    "{t.text}"
                  </p>
                  <div className="text-xs" data-id="element-357">
                    <span className="font-extrabold text-slate-900" data-id="element-358">
                      {t.name}
                    </span>
                    <span className="text-slate-300 mx-1.5" data-id="element-359">|</span>
                    <span className="text-slate-500 font-medium" data-id="element-360">{t.loc}</span>
                    <span className="text-slate-300 mx-1.5" data-id="element-361">|</span>
                    <span className="text-primary-dark font-bold" data-id="element-362">
                      {t.plan}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>
        </FadeIn>

        {/* Guarantee */}
        <FadeIn delay={0.5} data-id="element-363">
          <div className="max-w-3xl mx-auto bg-white rounded-3xl p-8 md:p-10 border border-slate-200 text-center shadow-soft-lg relative overflow-hidden mb-6" data-id="element-364">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6 text-3xl" data-id="element-365">
              🛡️
            </div>
            <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900 mb-4" data-id="element-366">
              GARANTÍA 7 DÍAS — SIN PREGUNTAS
            </h3>
            <p className="text-slate-600 text-base md:text-lg mb-6 font-medium" data-id="element-367">
              Abrís los tomos. Los aplicás en la sesión de tarea con tu hijo. Si
              en 7 días no es lo que esperabas:
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6 text-slate-800 font-bold text-base" data-id="element-368">
              <div className="flex items-center justify-center gap-2.5" data-id="element-369">
                <CheckCircle2Icon className="text-primary w-5 h-5" data-id="element-370" /> Devolución
                del 100%
              </div>
              <div className="flex items-center justify-center gap-2.5" data-id="element-371">
                <CheckCircle2Icon className="text-primary w-5 h-5" data-id="element-372" /> Sin
                formularios, sin drama.
              </div>
            </div>
            <div className="inline-block bg-slate-50 px-5 py-2.5 rounded-xl border border-slate-200 text-xs text-slate-500 font-bold" data-id="element-373">
              Tasa de devoluciones: menos del 1%.
            </div>
          </div>
        </FadeIn>

        {/* FAQ */}
        <FadeIn delay={0.6} data-id="element-374">
          <div className="max-w-3xl mx-auto mt-14" data-id="element-375">
            <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-slate-900 mb-8 text-center" data-id="element-376">
              ❓ LO QUE MÁS NOS PREGUNTAN
            </h3>
            <div className="space-y-3" role="list" data-id="element-377">
              {[{
              q: '¿Sirve si el bloqueo de mi hijo no es emocional sino de base matemática?',
              a: 'Sí. El sistema cubre los 5 tipos de bloqueo: miedo al error, falta de base, explicación abstracta, saturación emocional y dependencia del adulto. El Tomo 1 incluye el diagnóstico para identificar cuál es el dominante y el protocolo específico para trabajarlo.'
            }, {
              q: '¿Cómo recibo el material?',
              a: 'Inmediatamente después de tu compra recibís un correo con el link de descarga de todos los PDFs. Acceso inmediato, sin esperas. Podés leerlos en tu teléfono, tablet o computadora.'
            }, {
              q: '¿Puedo imprimirlo?',
              a: 'Sí. Todos los materiales están diseñados para ser impresos y usados en la mesa de trabajo. Las tarjetas del Kit Acelerador, las fichas de misión y las plantillas están optimizadas para impresión en A4.'
            }, {
              q: '¿Sirve si recién empezamos a tener problemas con matemáticas?',
              a: 'Sí, y es el mejor momento para usarlo. Cuanto antes se detecta el tipo de bloqueo, más fácil es revertirlo antes de que se convierta en una creencia permanente sobre su capacidad.'
            }, {
              q: '¿Tiene garantía real?',
              a: 'Sí. 7 días de garantía total. Si en ese período no es lo que esperabas, escribinos a soporteoficialfegion@gmail.com y te devolvemos el 100% sin preguntas ni formularios.'
            }].map((faq, i) => <div key={i} className="bg-white border border-slate-200 rounded-2xl overflow-hidden transition-shadow hover:shadow-soft" role="listitem" data-id="element-378">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-6 py-5 text-left flex items-center justify-between focus-visible:outline-2 focus-visible:outline-primary" aria-expanded={openFaq === i} data-id="element-379">
                    <span className="font-extrabold text-slate-900 text-base pr-4" data-id="element-380">
                      {faq.q}
                    </span>
                    <ChevronDownIcon className={`w-5 h-5 text-primary transition-transform shrink-0 ${openFaq === i ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-381" />
                  </button>
                  {openFaq === i && <div className="px-6 pb-6 text-slate-600 text-sm md:text-base leading-relaxed border-t border-slate-100 pt-4" data-id="element-382">
                      {faq.a}
                    </div>}
                </div>)}
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}